/**
 * Storage Error Types
 * Ported from: innovation-claude/packages/storage/src/error.rs
 *
 * Comprehensive error handling for all storage operations.
 */

/**
 * Storage error codes
 */
export type StorageErrorCode =
    | 'NETWORK'
    | 'AUTHENTICATION'
    | 'AUTHORIZATION'
    | 'SERIALIZATION'
    | 'DESERIALIZATION'
    | 'BACKEND_UNAVAILABLE'
    | 'BACKEND_NOT_FOUND'
    | 'ITEM_NOT_FOUND'
    | 'CATEGORY_NOT_FOUND'
    | 'INVALID_PATH'
    | 'MISSING_NAME_FIELD'
    | 'INVALID_NAME_FIELD'
    | 'QUOTA_EXCEEDED'
    | 'CONCURRENT_MODIFICATION'
    | 'INVALID_DATA'
    | 'TIMEOUT'
    | 'PLATFORM'
    | 'FILE_SYSTEM'
    | 'CONFIGURATION'
    | 'SYNCHRONIZATION_FAILED'
    | 'MIGRATION'
    | 'IO'
    | 'UNKNOWN'

/**
 * Storage error class with detailed error information
 * Ported from error.rs:14-100
 */
export class StorageError extends Error {
    public readonly code: StorageErrorCode
    public readonly statusCode?: number
    public readonly cause?: Error

    constructor(
        message: string,
        code: StorageErrorCode,
        options?: {
            statusCode?: number
            cause?: Error
        }
    ) {
        super(message)
        this.name = 'StorageError'
        this.code = code
        this.statusCode = options?.statusCode
        this.cause = options?.cause
    }

    /**
     * Create network error
     */
    static network(message: string, statusCode?: number): StorageError {
        return new StorageError(message, 'NETWORK', { statusCode })
    }

    /**
     * Create authentication error
     */
    static authentication(message: string): StorageError {
        return new StorageError(message, 'AUTHENTICATION')
    }

    /**
     * Create authorization error
     */
    static authorization(message: string): StorageError {
        return new StorageError(message, 'AUTHORIZATION')
    }

    /**
     * Create serialization error
     */
    static serialization(message: string, cause?: Error): StorageError {
        return new StorageError(message, 'SERIALIZATION', { cause })
    }

    /**
     * Create deserialization error
     */
    static deserialization(message: string, cause?: Error): StorageError {
        return new StorageError(message, 'DESERIALIZATION', { cause })
    }

    /**
     * Create backend unavailable error
     */
    static backendUnavailable(backendName: string): StorageError {
        return new StorageError(`Storage backend '${backendName}' is not available`, 'BACKEND_UNAVAILABLE')
    }

    /**
     * Create item not found error
     */
    static itemNotFound(itemName: string, category: string): StorageError {
        return new StorageError(`Item '${itemName}' not found in category '${category}'`, 'ITEM_NOT_FOUND')
    }

    /**
     * Create timeout error
     */
    static timeout(seconds: number): StorageError {
        return new StorageError(`Operation timed out after ${seconds} seconds`, 'TIMEOUT')
    }

    /**
     * Create platform error
     */
    static platform(message: string): StorageError {
        return new StorageError(`Platform error: ${message}`, 'PLATFORM')
    }

    /**
     * Create quota exceeded error
     */
    static quotaExceeded(message: string): StorageError {
        return new StorageError(`Storage quota exceeded: ${message}`, 'QUOTA_EXCEEDED')
    }
}

/**
 * Result type for storage operations
 */
export type StorageResult<T> = Promise<T>
